import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Divider,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Fab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Add as AddIcon,
    Bolt as BoltIcon,
    WarningAmber as WarningIcon,
    CheckCircle as CheckIcon
} from '@mui/icons-material';
import {
    fetchLicenses,
    toggleActivation,
    deleteLicense,
    fetchDealers
} from '../api/licenseService';
import EditLicenseDialog from './EditLicenseDialog';
import CreateLicenseDialog from './CreateLicenseDialog';
import FilterBar from './FilterBar';

function LicenseDashboard() {
    const [licenses, setLicenses] = useState([]);
    const [filteredLicenses, setFilteredLicenses] = useState([]);
    const [dealers, setDealers] = useState([]);
    const [filterDealerId, setFilterDealerId] = useState('');
    const [selectedLicense, setSelectedLicense] = useState(null);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [createParentLicense, setCreateParentLicense] = useState(null);
    const [licenseToDelete, setLicenseToDelete] = useState(null);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const isMaster = localStorage.getItem('isMaster') === 'True';

    const loadLicenses = async (dealerId = filterDealerId) => {
        const all = await fetchLicenses();

        // Build a map of tillId -> distributorId for main licenses
        const distributorMap = {};
        all.filter(l => l.product === 'SuperPos').forEach(l => {
            distributorMap[l.tillId] = l.distributor?.id || null;
        });

        setLicenses(all);

        setFilteredLicenses(
            dealerId
                ? all.filter((l) => {
                    const distId = l.distributor?.id || distributorMap[l.tillId];
                    return distId === dealerId;
                })
                : all
        );
    };

    const loadDealers = async () => {
        if (isMaster) {
            const res = await fetchDealers(localStorage.getItem('apiKey'));
            setDealers(res || []);
        }
    };

    useEffect(() => {
        loadLicenses();
        loadDealers();
    }, []);

    const handleDelete = async () => {
        const dealerPassword = localStorage.getItem('password');
        await deleteLicense(licenseToDelete.id, dealerPassword);
        setSnackbarMessage('Lizenz gelöscht');
        setOpenSnackbar(true);
        setLicenseToDelete(null);
        await loadLicenses();
    };

    const handleToggleActivation = async (license) => {
        await toggleActivation(license);
        setSnackbarMessage(`Lizenz ${license.isActive ? 'deaktiviert' : 'aktiviert'}`);
        setOpenSnackbar(true);
        await loadLicenses();
    };

    const isExpired = (validUntil) => validUntil && new Date(validUntil) < new Date();

    const superPosLicenses = filteredLicenses.filter((l) => l.product === 'SuperPos');

    const getFeaturesFor = (main) =>
        licenses.filter((l) => l.product !== 'SuperPos' && l.tillId === main.tillId);

    const getAvailableFeatureProducts = (mainLicense) => {
        const usedProducts = licenses
            .filter((l) => l.tillId === mainLicense.tillId && l.product !== 'SuperPos')
            .map((l) => l.product);

        return ['Lieferando'].filter((p) => !usedProducts.includes(p));
    };

    return (
        <Box p={2}>
            <Typography variant="h4" gutterBottom>
                Lizenzübersicht
            </Typography>

            {isMaster && (
                <FormControl fullWidth margin="normal">
                    <InputLabel>Nach Händler filtern</InputLabel>
                    <Select
                        value={filterDealerId}
                        onChange={(e) => {
                            const newDealerId = e.target.value;
                            setFilterDealerId(newDealerId);
                            loadLicenses(newDealerId);
                        }}
                    >
                        <MenuItem value="">Alle Händler</MenuItem>
                        {dealers.map((dealer) => (
                            <MenuItem key={dealer.id} value={dealer.id}>
                                {dealer.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            <FilterBar allLicenses={licenses} setLicenses={setFilteredLicenses} />

            <Grid container spacing={2} direction="column" mt={2}>
                {superPosLicenses.map((main) => {
                    const features = getFeaturesFor(main);
                    const availableFeatures = getAvailableFeatureProducts(main);

                    return (
                        <Grid item key={main.id}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6">{main.owner}</Typography>
                                    <Typography variant="body2">Lizenztyp: {main.licenseType}</Typography>
                                    <Typography variant="body2">Gültig bis: {main.validUntil}</Typography>
                                    <Typography variant="body2">Aktiv: {main.isActive ? 'Ja' : 'Nein'}</Typography>

                                    {isMaster && (
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Händler</InputLabel>
                                            <Select
                                                value={main.distributor?.id || ''}
                                                onChange={(e) => {
                                                    const newDealer = dealers.find(d => d.id === e.target.value);
                                                    setSelectedLicense({
                                                        ...main,
                                                        distributor: newDealer
                                                    });
                                                }}
                                            >
                                                {dealers.map((d) => (
                                                    <MenuItem key={d.id} value={d.id}>
                                                        {d.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    <Box display="flex" gap={1} mt={2}>
                                        <Tooltip title="Bearbeiten">
                                            <IconButton onClick={() => setSelectedLicense(main)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Löschen">
                                            <IconButton onClick={() => setLicenseToDelete(main)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={main.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                                            <IconButton onClick={() => handleToggleActivation(main)}>
                                                <BoltIcon color={main.isActive ? 'warning' : 'disabled'} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                            title={availableFeatures.length === 0
                                                ? 'Alle Features bereits hinzugefügt'
                                                : 'Feature hinzufügen'}
                                        >
                                            <span>
                                                <IconButton
                                                    onClick={() => {
                                                        setCreateParentLicense(main);
                                                        setShowCreateDialog(true);
                                                    }}
                                                    disabled={availableFeatures.length === 0}
                                                >
                                                    <AddIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>

                                    {features.length > 0 && (
                                        <>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle1">Feature-Lizenzen</Typography>
                                            <List dense>
                                                {features.map((f) => (
                                                    <ListItem
                                                        key={f.id}
                                                        secondaryAction={
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Tooltip
                                                                    title={`Ablaufdatum: ${f.validUntil ?? 'Unbekannt'}`}
                                                                    placement="top"
                                                                >
                                                                    {isExpired(f.validUntil) ? (
                                                                        <WarningIcon color="warning" fontSize="small" />
                                                                    ) : (
                                                                        <CheckIcon color="success" fontSize="small" />
                                                                    )}
                                                                </Tooltip>

                                                                <Tooltip title="Bearbeiten">
                                                                    <IconButton size="small" onClick={() => setSelectedLicense(f)}>
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>

                                                                <Tooltip title={f.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleToggleActivation(f)}
                                                                    >
                                                                        <BoltIcon
                                                                            fontSize="small"
                                                                            color={f.isActive ? 'warning' : 'disabled'}
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>

                                                                <Tooltip title="Löschen">
                                                                    <IconButton size="small" onClick={() => setLicenseToDelete(f)}>
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        }
                                                    >
                                                        <ListItemText
                                                            primary={`${f.product} (${f.licenseType})`}
                                                            secondary={`ID: ${f.id}`}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Fab
                color="primary"
                sx={{ position: 'fixed', bottom: 32, right: 32 }}
                onClick={() => {
                    setCreateParentLicense(null);
                    setShowCreateDialog(true);
                }}
            >
                <AddIcon />
            </Fab>

            {selectedLicense && (
                <EditLicenseDialog
                    license={selectedLicense}
                    onClose={() => setSelectedLicense(null)}
                    onSave={async (updatedLicense) => {
                        const { updateLicense } = await import('../api/licenseService');
                        const token = localStorage.getItem('token');
                        await updateLicense(updatedLicense, token);
                        setSelectedLicense(null);
                        await loadLicenses();
                    }}
                />
            )}

            {showCreateDialog && (
                <CreateLicenseDialog
                    parentLicense={createParentLicense}
                    onClose={() => {
                        setShowCreateDialog(false);
                        setCreateParentLicense(null);
                    }}
                    onCreated={() => {
                        setShowCreateDialog(false);
                        setCreateParentLicense(null);
                        loadLicenses();
                    }}
                />
            )}

            {licenseToDelete && (
                <Dialog open onClose={() => setLicenseToDelete(null)}>
                    <DialogTitle>Lizenz löschen</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Möchten Sie diese Lizenz wirklich löschen?
                            <br />
                            <strong>ID:</strong> {licenseToDelete.id}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setLicenseToDelete(null)}>Abbrechen</Button>
                        <Button onClick={handleDelete} variant="contained" color="error">
                            Löschen
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                message={snackbarMessage}
            />
        </Box>
    );
}

export default LicenseDashboard;
